import { useQueries } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Clock, Info, LibraryBig, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { AvailableActionCatalogItem } from '@/components/features/automation/available-action-catalog-item';
import { NewAutomationDialog } from '@/components/features/automation/dialogs/new-automation-dialog';
import { ScheduleActionItem } from '@/components/features/automation/schedule-action';
import { useCurrentOrganization } from '@/components/features/membership/membership-provider';
import { Can } from '@/components/features/permission/can';
import ScheduleActionsLimit from '@/components/features/schedule/schedule-actions-limit';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TabsContent } from '@/components/ui/tabs';
import { getAvailableActionsQuery } from '@/queries/action/getAvailableActionsQuery';
import { getScheduleActionsQuery } from '@/queries/action/getScheduleActionsQuery';
import {
  getOrganizationIntegrationsQuery,
  integrationsByProviderName,
} from '@/queries/integration/getOrganizationIntegrationsQuery';

export const Route = createFileRoute(
  '/_authenticated/schedule/$scheduleId/automations'
)({
  component: AutomationsPage,
});

function AutomationsPage() {
  const { scheduleId } = Route.useParams();
  const { organizationId } = useCurrentOrganization();
  const { t } = useTranslation();

  const [
    { data: availableActions },
    { data: connectedIntegrationsByProviderName },
    { data: scheduleActions },
  ] = useQueries({
    queries: [
      getAvailableActionsQuery({
        organizationId,
      }),
      {
        ...getOrganizationIntegrationsQuery(organizationId),
        select: integrationsByProviderName,
      },
      getScheduleActionsQuery({
        organizationId,
        scheduleId,
      }),
    ],
  });

  return (
    <TabsContent value="automations" className="space-y-6">
      <ScheduleActionsLimit
        organizationId={organizationId}
        scheduleId={scheduleId}
      >
        {(limitExceeded) => (
          <>
            {/* Info Block */}
            <Card className="border-blue-200/30 bg-blue-50/10">
              <CardContent className="">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-medium mb-1">
                      {t(
                        'authenticated.schedule.details.automations.whatAreAutomations'
                      )}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {t(
                        'authenticated.schedule.details.automations.automationsDescription'
                      )}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Current Automations Section */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <CardTitle className="flex items-center">
                    <Zap className="h-5 w-5 mr-2" />
                    {t(
                      'authenticated.schedule.details.automations.scheduleAutomations'
                    )}
                  </CardTitle>
                  <ScheduleActionsLimit.Badge />
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-2">
                  {scheduleActions.length > 0 ? (
                    scheduleActions.map((action) => (
                      <ScheduleActionItem
                        key={action.actionId}
                        action={action}
                        scheduleId={scheduleId}
                      />
                    ))
                  ) : (
                    <div className="flex flex-col items-center justify-center py-5 text-center border border-dashed rounded-md">
                      <p className="text-sm text-muted-foreground">
                        {t(
                          'authenticated.schedule.details.automations.noAutomations'
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t(
                          'authenticated.schedule.details.automations.addYourFirstAutomation'
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Automation Catalog Section */}
            <Can permissions={['action:create']}>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center">
                    <LibraryBig className="mr-2 h-5 w-5" />
                    {t('authenticated.schedule.details.automations.catalog')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {availableActions.map((action) => {
                      const providerConnected =
                        connectedIntegrationsByProviderName?.[action.provider]
                          ?.integrationId !== undefined;

                      return (
                        <NewAutomationDialog
                          key={action.type}
                          scheduleId={scheduleId}
                          disabled={limitExceeded}
                          providerConnected={providerConnected}
                          trigger={
                            <AvailableActionCatalogItem
                              action={action}
                              providerConnected={providerConnected}
                              disabled={limitExceeded}
                            />
                          }
                          action={action}
                        />
                      );
                    })}

                    {/* More coming soon item */}
                    <div className="flex items-center justify-between p-3 border rounded-md opacity-75 bg-muted/30 cursor-not-allowed">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center h-8 w-8 rounded-md bg-background/80">
                          <Clock className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div>
                          <h3 className="text-sm font-medium">
                            {t(
                              'authenticated.schedule.details.automations.moreComingSoon.title'
                            )}
                          </h3>
                          <p className="text-xs text-muted-foreground">
                            {t(
                              'authenticated.schedule.details.automations.moreComingSoon.description'
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Can>
          </>
        )}
      </ScheduleActionsLimit>
    </TabsContent>
  );
}
